import { forwardRef, useId, type SelectHTMLAttributes } from 'react'
import { FIELD_SIZE } from '@/components/ui/Input'
import { cn } from '@/lib/cn'

export interface SelectOption {
  value: string
  label: string
  /** an `<optgroup>` label: options of one group are listed under it, in their order */
  group?: string
  disabled?: boolean
}

/** Options without a group first, then each group under its `<optgroup>`. */
function grouped(options: readonly SelectOption[]): { group: string | null; options: SelectOption[] }[] {
  const out: { group: string | null; options: SelectOption[] }[] = []
  for (const o of options) {
    const key = o.group ?? null
    const last = out.find((g) => g.group === key)
    if (last) last.options.push(o)
    else out.push({ group: key, options: [o] })
  }
  return out.sort((a, b) => (a.group === null ? -1 : b.group === null ? 1 : 0))
}

/** Control sizes: `md` is the form field (44 px, body-lg like `Input`), `sm` the 40 px FilterBar control, `xs` a
 *  32 px control inside a dense row (a media item's role) — 44 px on a touch screen. */
const SIZE = {
  md: FIELD_SIZE.md,
  sm: FIELD_SIZE.sm,
  xs: 'h-8 px-2 text-caption [@media(pointer:coarse)]:h-11',
} as const

export type SelectSize = keyof typeof SIZE

export interface SelectControlProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange' | 'size' | 'children'> {
  value: string
  options: readonly SelectOption[]
  onChange: (value: string) => void
  size?: SelectSize
}

/**
 * The bare control of the admin's one select (design §6.3: the field line, a cyan focus ring,
 * a neutral disabled look). For a field that already has its label and description wiring
 * (`FormField`, a form's own `Field`): pass its `id` / `aria-*` props through. Everywhere else
 * use `Select`, which brings its visible label.
 */
export const SelectControl = forwardRef<HTMLSelectElement, SelectControlProps>(
  ({ value, options, onChange, size = 'md', className, ...rest }, ref) => (
    <select
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'w-full min-w-0 rounded-sm border border-field bg-surface-3 px-3 text-ink-1',
        SIZE[size],
        'focus-visible:border-cyan focus-visible:shadow-glow-cyan focus-visible:outline-none',
        'aria-[invalid=true]:border-danger',
        'disabled:border-hairline disabled:text-ink-3',
        className,
      )}
      {...rest}
    >
      {grouped(options).map(({ group, options: items }) => {
        const list = items.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))
        return group === null ? (
          list
        ) : (
          <optgroup key={group} label={group}>
            {list}
          </optgroup>
        )
      })}
    </select>
  ),
)
SelectControl.displayName = 'SelectControl'

/** A labelled native select for filter rows and forms (design §6.3: field line, cyan focus). The
 *  label is always visible above the control — a placeholder never replaces it (`hideLabel`
 *  keeps it for screen readers only, where a toolbar names the control otherwise). `size="sm"`
 *  is the 40 px FilterBar control (WP-14). */
export function Select({
  label,
  value,
  options,
  onChange,
  disabled,
  size = 'md',
  hideLabel,
  id: fixedId,
  className,
}: {
  label: string
  value: string
  options: readonly SelectOption[]
  onChange: (value: string) => void
  disabled?: boolean
  size?: SelectSize
  hideLabel?: boolean
  id?: string
  className?: string
}) {
  const auto = useId()
  const id = fixedId ?? auto
  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'mb-1.5 block text-caption text-ink-2'}>
        {label}
      </label>
      <SelectControl id={id} value={value} options={options} onChange={onChange} disabled={disabled} size={size} />
    </div>
  )
}
